import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as OTPAuth from 'otpauth';
import jsQR from 'jsqr';
import QRCode from 'qrcode';
import { useParams } from 'react-router-dom';
import './App.css';
import { configureCodeTelemetry, noteCodesObtained, noteSecretsEntered } from './codeTelemetry';
import {
  AccountExportFormat,
  LocalTwoFaAccount,
  TotpShareConfig,
  buildAccountExportText,
  buildOtpAuthUri,
  buildPermanentLink,
  normalizeBase32Secret,
  normalizePositiveNumber,
  normalizeAlgorithm,
  generateLocalId,
  buildLocalDisplayName,
  maskSecret,
  parseTotpSource,
  parseFlexibleLineClient,
  transformBulkInputToPermanentLinks,
} from './totp';

interface TwoFaAccount {
  id: number;
  user_id: string;
  account_note: string;
  source_raw: string;
  secret_base32: string;
  secret_masked: string;
  issuer: string;
  label: string;
  algorithm: string;
  digits: number;
  period: number;
  created_at: string;
  updated_at: string;
}

interface RuntimeTwoFaAccount extends TwoFaAccount {
  current_code: string;
  remaining_seconds: number;
  display_name: string;
  search_blob: string;
}

interface BulkCreatedItem {
  line: number;
  account: TwoFaAccount;
}

interface BulkSkippedItem {
  line: number;
  reason: string;
  input: string;
}

interface BulkErrorItem {
  line: number;
  reason: string;
  input: string;
}

interface BulkResponse {
  created: BulkCreatedItem[];
  skipped: BulkSkippedItem[];
  errors: BulkErrorItem[];
}

// Рантайм-версия локального аккаунта с живым TOTP-кодом
interface RuntimeLocalAccount extends LocalTwoFaAccount {
  current_code: string;
  remaining_seconds: number;
  search_blob: string;
}

const ACTIVE_SERVICE_SLUG = 'two-fa';

type AppIconName = 'alert' | 'arrow-left' | 'arrow-right' | 'camera' | 'check' | 'chevron-down' | 'copy' | 'shield' | 'trash';

function AppIcon({ name, className = '' }: { name: AppIconName; className?: string }) {
  const paths: Record<AppIconName, React.ReactNode> = {
    alert: <><circle cx="12" cy="12" r="9" /><path d="M12 7v6M12 17h.01" /></>,
    'arrow-left': <><path d="m14.5 6-6 6 6 6" /><path d="M9 12h9" /></>,
    'arrow-right': <><path d="m9.5 6 6 6-6 6" /><path d="M6 12h9" /></>,
    camera: <><path d="M4 8.5h3l1.5-2h7l1.5 2h3v9H4z" /><circle cx="12" cy="13" r="3.25" /></>,
    check: <path d="m5 12 4 4 10-10" />,
    'chevron-down': <path d="m6 9 6 6 6-6" />,
    copy: <><rect x="8" y="8" width="10" height="10" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></>,
    shield: <><path d="M12 3 5 6v5c0 4.5 2.7 7.8 7 10 4.3-2.2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>,
    trash: <><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5" /></>,
  };

  return (
    <svg
      className={`fa-icon ${className}`.trim()}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

function isLocalDevBrowser(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const { hostname, port } = window.location;
  return (hostname === 'localhost' || hostname === '127.0.0.1')
    && (port === '3000' || port === '3001');
}

function getAuthVerifyUrl(): string {
  if (typeof window === 'undefined') {
    return '/2fa/api/auth/verify';
  }

  if (isLocalDevBrowser()) {
    return 'http://localhost:5006/api/auth/verify';
  }

  return '/2fa/api/auth/verify';
}

function getTwoFaApiBaseUrl(): string {
  if (typeof window === 'undefined') {
    return '/2fa/api/twofa';
  }

  if (isLocalDevBrowser()) {
    return 'http://localhost:5006/api/twofa';
  }

  return '/2fa/api/twofa';
}

function getStoredToken(): string {
  try {
    return (localStorage.getItem('token') || '').trim();
  } catch {
    return '';
  }
}

function getStoredRefreshToken(): string {
  try {
    return (localStorage.getItem('refreshToken') || '').trim();
  } catch {
    return '';
  }
}

function getAuthRefreshUrl(): string {
  if (typeof window === 'undefined') {
    return '/api/auth/refresh';
  }
  if (isLocalDevBrowser()) {
    return 'http://localhost:5006/api/auth/refresh';
  }
  return '/api/auth/refresh';
}

// Попытка обновить access-токен через refresh-token.
// Возвращает новый access-токен или null при неудаче.
async function tryRefreshToken(refreshToken: string): Promise<string | null> {
  try {
    const response = await fetch(getAuthRefreshUrl(), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    const newToken: unknown = data?.token || data?.data?.accessToken;
    if (typeof newToken === 'string' && newToken.trim()) {
      localStorage.setItem('token', newToken.trim());
      return newToken.trim();
    }
    return null;
  } catch {
    return null;
  }
}

function buildAuthHeaders(token: string, asJson: boolean): HeadersInit {
  const headers: Record<string, string> = {
    accept: 'application/json',
    authorization: `Bearer ${token}`,
  };

  if (asJson) {
    headers['content-type'] = 'application/json';
  }

  return headers;
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function fallbackCopyWithExecCommand(text: string): boolean {
  if (typeof document === 'undefined' || !document.body) {
    return false;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '-9999px';
  textarea.style.opacity = '0';

  document.body.appendChild(textarea);

  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  if (!text) {
    return false;
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Ошибку clipboard обрабатываем fallback-механизмом ниже.
    }
  }

  return fallbackCopyWithExecCommand(text);
}

function replaceAddressBarSecret(secretBase32: string | null): void {
  if (typeof window === 'undefined') return;

  const currentPath = window.location.pathname;
  const hasTwoFaPrefix = currentPath === '/2fa' || currentPath.startsWith('/2fa/');
  const basePath = hasTwoFaPrefix ? '/2fa' : '';
  const nextPath = secretBase32
    ? `${basePath}/${encodeURIComponent(normalizeBase32Secret(secretBase32))}`
    : `${basePath}/`;

  if (currentPath !== nextPath) {
    window.history.replaceState(window.history.state, '', nextPath);
  }
}

function PermanentAddress({ secretBase32, compact = false }: { secretBase32: string; compact?: boolean }) {
  const [copyStatus, setCopyStatus] = useState('');
  const timeoutRef = useRef<number | null>(null);
  const url = useMemo(() => buildPermanentLink(secretBase32), [secretBase32]);

  useEffect(() => () => {
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
  }, []);

  const handleCopy = async () => {
    const copied = await copyTextToClipboard(url);
    setCopyStatus(copied ? 'Скопировано' : 'Ошибка');
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => setCopyStatus(''), 1600);
  };
  const copyLabel = copyStatus || 'Копировать постоянный адрес';
  const copyIcon = copyStatus ? (copyStatus === 'Скопировано' ? 'check' : 'alert') : 'copy';

  if (!compact) {
    return (
      <div className="fa-permanent">
        <div className="fa-permanent-head">
          <span className="fa-permanent-label">Постоянная ссылка</span>
          <span className="fa-permanent-hint">Откройте этот генератор на другом устройстве.</span>
        </div>
        <div className="fa-permanent-control">
          <a className="fa-permanent-url mono" href={url} target="_blank" rel="noreferrer" title={url}>{url}</a>
          <button
            type="button"
            className="fa-permanent-copy"
            onClick={handleCopy}
            title="Скопировать постоянную ссылку"
            aria-label={copyLabel}
          >
            <AppIcon name={copyIcon} />
            <span>{copyStatus || 'Копировать'}</span>
          </button>
        </div>
        <span className="sr-only" aria-live="polite">{copyStatus}</span>
      </div>
    );
  }

  return (
    <div className="fa-permanent is-compact">
      <span className="fa-permanent-label">Постоянный адрес</span>
      <a className="fa-permanent-url mono" href={url} target="_blank" rel="noreferrer" title={url}>{url}</a>
      <button
        type="button"
        className="fa-permanent-copy"
        onClick={handleCopy}
        title="Скопировать постоянный адрес"
        aria-label={copyLabel}
      >
        <AppIcon name={copyIcon} />
      </button>
      <span className="sr-only" aria-live="polite">{copyStatus}</span>
    </div>
  );
}

function TotpQrCode({
  config,
  compact = false,
  title = 'QR для приложения-аутентификатора',
}: {
  config: TotpShareConfig;
  compact?: boolean;
  title?: string;
}) {
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState('');
  const {
    secretBase32,
    issuer,
    label,
    algorithm,
    digits,
    period,
    fallbackLabel,
  } = config;
  const otpAuthUri = useMemo(() => buildOtpAuthUri({
    secretBase32,
    issuer,
    label,
    algorithm,
    digits,
    period,
    fallbackLabel,
  }), [algorithm, digits, fallbackLabel, issuer, label, period, secretBase32]);

  useEffect(() => {
    let cancelled = false;
    setImageUrl('');
    setError('');

    QRCode.toDataURL(otpAuthUri, {
      width: compact ? 160 : 196,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#0f172a', light: '#ffffff' },
    }).then((url) => {
      if (!cancelled) setImageUrl(url);
    }).catch(() => {
      if (!cancelled) setError('Не удалось создать QR-код');
    });

    return () => { cancelled = true; };
  }, [compact, otpAuthUri]);

  return (
    <div className={`fa-generated-qr ${compact ? 'is-compact' : ''}`}>
      <div className="fa-generated-qr-title">{title}</div>
      {imageUrl ? <img src={imageUrl} alt="QR-код TOTP" width={compact ? 160 : 196} height={compact ? 160 : 196} /> : null}
      {!imageUrl && !error ? <div className="fa-generated-qr-status" role="status">Создаём QR…</div> : null}
      {error ? <div className="fa-generated-qr-status is-error" role="alert">{error}</div> : null}
    </div>
  );
}

function SingleSharePanel({ config }: { config: TotpShareConfig }) {
  return (
    <section className="fa-share-panel" aria-labelledby="share-panel-title">
      <header className="fa-share-header">
        <div>
          <h2 id="share-panel-title">Сохранить или перенести 2FA</h2>
          <p>Отсканируйте QR-код или сохраните постоянную ссылку.</p>
        </div>
      </header>
      <div className="fa-share-content">
        <div className="fa-share-qr">
          <TotpQrCode
            config={config}
            title="Сканировать QR"
          />
        </div>
        <div className="fa-share-link">
          <PermanentAddress secretBase32={config.secretBase32} />
          <div className="fa-share-safety">
            <AppIcon name="shield" />
            <span>Ссылка содержит секрет 2FA. Храните её как пароль и не публикуйте.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

interface ExportControlsProps {
  format: AccountExportFormat;
  selectedCount: number;
  onFormatChange: (format: AccountExportFormat) => void;
  onExport: () => void;
}

function ExportControls({
  format,
  selectedCount,
  onFormatChange,
  onExport,
}: ExportControlsProps) {
  return (
    <div className="fa-export-controls">
      <select
        name="export-format"
        value={format}
        onChange={(event) => onFormatChange(event.target.value as AccountExportFormat)}
        aria-label="Формат экспорта"
      >
        <option value="links">Постоянные ссылки</option>
        <option value="secrets">Секреты 2FA</option>
        <option value="rows">Строки целиком</option>
      </select>
      <button type="button" className="fa-btn-export" onClick={onExport} disabled={selectedCount === 0}>
        Экспорт{selectedCount > 0 ? ` (${selectedCount})` : ''}
      </button>
    </div>
  );
}

function AccountTableHeader() {
  return (
    <div className="fa-table-columns" aria-hidden="true">
      <span />
      <span>Аккаунт</span>
      <span>Постоянная ссылка</span>
      <span className="is-code">Код</span>
      <span className="is-actions">Действия</span>
    </div>
  );
}

interface AccountTableRowProps {
  displayName: string;
  note?: string;
  secretBase32: string;
  currentCode: string;
  remainingSeconds: number;
  copyStatus?: string;
  selected: boolean;
  urgent: boolean;
  expanded: boolean;
  avatarColor: string;
  shareConfig: TotpShareConfig;
  details: React.ReactNode;
  onSelect: () => void;
  onToggle: () => void;
  onCopyCode: () => void;
  onDelete: () => void;
}

function AccountTableRow({
  displayName,
  note,
  secretBase32,
  currentCode,
  remainingSeconds,
  copyStatus,
  selected,
  urgent,
  expanded,
  avatarColor,
  shareConfig,
  details,
  onSelect,
  onToggle,
  onCopyCode,
  onDelete,
}: AccountTableRowProps) {
  return (
    <div className={`fa-acc-row ${urgent ? 'is-urgent' : ''} ${selected ? 'is-selected' : ''}`}>
      <input
        type="checkbox"
        className="fa-acc-check"
        checked={selected}
        onChange={onSelect}
        aria-label={`Выбрать ${displayName}`}
      />
      <div className="fa-acc-identity">
        <div className="fa-acc-avatar" style={{ background: avatarColor }}>
          {displayName.charAt(0).toUpperCase() || '2'}
        </div>
        <div className="fa-acc-info">
          <div className="fa-acc-name" title={displayName}>{displayName}</div>
          <div className="fa-acc-sub fa-acc-secret">{secretBase32}</div>
          {note ? <div className="fa-acc-sub" title={note}>{note}</div> : null}
        </div>
      </div>
      <PermanentAddress secretBase32={secretBase32} compact />
      <div className="fa-acc-code-col">
        <button
          type="button"
          className={`fa-acc-code is-clickable ${copyStatus ? 'is-copied' : ''}`}
          onClick={onCopyCode}
          title="Скопировать текущий код"
          aria-label={`Скопировать код для ${displayName}`}
        >
          {copyStatus || currentCode}
        </button>
        <div className="fa-acc-time">{currentCode === '------' ? 0 : remainingSeconds}с</div>
      </div>
      <div className="fa-acc-actions">
        <button
          type="button"
          className={`fa-btn-row-qr ${expanded ? 'is-active' : ''}`}
          onClick={onToggle}
          title={expanded ? 'Скрыть QR-код' : 'Показать QR-код'}
          aria-label={expanded ? 'Скрыть QR-код' : 'Показать QR-код'}
          aria-expanded={expanded}
        >
          QR
        </button>
        <button
          type="button"
          className={`fa-expand-btn ${expanded ? 'is-open' : ''}`}
          onClick={onToggle}
          title="Подробнее"
          aria-label="Подробнее"
          aria-expanded={expanded}
        >
          <AppIcon name="chevron-down" />
        </button>
        <button type="button" className="fa-btn-del" onClick={onDelete} title="Удалить" aria-label="Удалить аккаунт">
          <AppIcon name="trash" />
        </button>
      </div>
      {expanded ? (
        <div className="fa-acc-details">
          <div className="fa-acc-details-content">{details}</div>
          <TotpQrCode config={shareConfig} compact />
        </div>
      ) : null}
    </div>
  );
}

function downloadTextFile(text: string, filename: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

function exportFilename(format: AccountExportFormat): string {
  const date = new Date().toISOString().slice(0, 10);
  return `2fa-${format}-${date}.txt`;
}

async function safeReadJson(response: Response): Promise<any> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function buildRuntimeLocalAccount(account: LocalTwoFaAccount, nowTimestamp: number): RuntimeLocalAccount {
  const epochSeconds = Math.floor(nowTimestamp / 1000);
  const period = normalizePositiveNumber(account.period, 30);

  try {
    const totp = new OTPAuth.TOTP({
      issuer: account.issuer || '',
      label: account.label || '',
      secret: OTPAuth.Secret.fromBase32(account.secretBase32),
      digits: normalizePositiveNumber(account.digits, 6),
      period,
      algorithm: normalizeAlgorithm(account.algorithm),
    });

    const currentCode = totp.generate({ timestamp: epochSeconds * 1000 });
    const remainingSeconds = period - (epochSeconds % period);
    const masked = maskSecret(account.secretBase32);

    return {
      ...account,
      current_code: currentCode,
      remaining_seconds: remainingSeconds,
      search_blob: `${account.displayName} ${masked} ${currentCode}`.toLowerCase(),
    };
  } catch {
    return {
      ...account,
      current_code: '------',
      remaining_seconds: 0,
      search_blob: `${account.displayName} ${maskSecret(account.secretBase32)}`.toLowerCase(),
    };
  }
}

function loadImageFromObjectUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Не удалось загрузить изображение'));
    image.src = url;
  });
}

async function readQrTextFromFile(file: File): Promise<string> {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageFromObjectUrl(objectUrl);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    if (width <= 0 || height <= 0) {
      throw new Error('Некорректное изображение');
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      throw new Error('Не удалось подготовить изображение для QR');
    }

    context.drawImage(image, 0, 0, width, height);

    const imageData = context.getImageData(0, 0, width, height);
    const qrCode = jsQR(imageData.data, imageData.width, imageData.height);

    if (!qrCode?.data) {
      throw new Error('QR не распознан');
    }

    return qrCode.data.trim();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function normalizeAccountFromApi(item: any): TwoFaAccount {
  return {
    id: Number(item?.id) || 0,
    user_id: String(item?.user_id || ''),
    account_note: typeof item?.account_note === 'string' ? item.account_note : '',
    source_raw: typeof item?.source_raw === 'string' ? item.source_raw : '',
    secret_base32: typeof item?.secret_base32 === 'string' ? item.secret_base32 : '',
    secret_masked: typeof item?.secret_masked === 'string'
      ? item.secret_masked
      : (typeof item?.secret_base32 === 'string' ? `${item.secret_base32.slice(0, 4)}••••${item.secret_base32.slice(-4)}` : ''),
    issuer: typeof item?.issuer === 'string' ? item.issuer : '',
    label: typeof item?.label === 'string' ? item.label : '',
    algorithm: normalizeAlgorithm(item?.algorithm),
    digits: normalizePositiveNumber(item?.digits, 6),
    period: normalizePositiveNumber(item?.period, 30),
    created_at: typeof item?.created_at === 'string' ? item.created_at : '',
    updated_at: typeof item?.updated_at === 'string' ? item.updated_at : '',
  };
}

function buildDisplayName(account: TwoFaAccount): string {
  const named = [account.issuer, account.label]
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .join(' / ');

  if (named) {
    return named;
  }

  // Логин из account_note (сервер извлекает текст вокруг секрета)
  const note = (account.account_note || '').trim();
  if (note && !/^Аккаунт\s+\d+$/i.test(note)) {
    return note;
  }

  // Первый токен из исходной строки (текст до первого пробела)
  const raw = (account.source_raw || '').trim();
  if (raw) {
    const firstSpaceIndex = raw.indexOf(' ');
    if (firstSpaceIndex > 0) {
      const firstToken = raw.slice(0, firstSpaceIndex).trim();
      if (firstToken && !/^[A-Z2-7]{16,}$/i.test(firstToken) && !/^otpauth:/i.test(firstToken)) {
        return firstToken;
      }
    }
  }

  return `Аккаунт #${account.id}`;
}

function buildRuntimeAccount(account: TwoFaAccount, nowTimestamp: number): RuntimeTwoFaAccount {
  const epochSeconds = Math.floor(nowTimestamp / 1000);
  const period = normalizePositiveNumber(account.period, 30);

  try {
    const totp = new OTPAuth.TOTP({
      issuer: account.issuer || '',
      label: account.label || '',
      secret: OTPAuth.Secret.fromBase32(account.secret_base32),
      digits: normalizePositiveNumber(account.digits, 6),
      period,
      algorithm: normalizeAlgorithm(account.algorithm),
    });

    const currentCode = totp.generate({ timestamp: epochSeconds * 1000 });
    const remainingSeconds = period - (epochSeconds % period);
    const displayName = buildDisplayName(account);

    return {
      ...account,
      display_name: displayName,
      current_code: currentCode,
      remaining_seconds: remainingSeconds,
      search_blob: `${displayName} ${account.account_note} ${account.secret_base32} ${account.secret_masked} ${currentCode}`.toLowerCase(),
    };
  } catch {
    const displayName = buildDisplayName(account);

    return {
      ...account,
      display_name: displayName,
      current_code: '------',
      remaining_seconds: 0,
      search_blob: `${displayName} ${account.account_note} ${account.secret_base32} ${account.secret_masked}`.toLowerCase(),
    };
  }
}

function localShareConfig(account: LocalTwoFaAccount): TotpShareConfig {
  return {
    secretBase32: account.secretBase32,
    issuer: account.issuer,
    label: account.label,
    algorithm: account.algorithm,
    digits: account.digits,
    period: account.period,
    fallbackLabel: account.note || account.displayName || '2FA',
  };
}

function serverShareConfig(account: TwoFaAccount): TotpShareConfig {
  return {
    secretBase32: account.secret_base32,
    issuer: account.issuer,
    label: account.label,
    algorithm: account.algorithm,
    digits: account.digits,
    period: account.period,
    fallbackLabel: account.account_note || '2FA',
  };
}

const App: React.FC = () => {
  const { seed } = useParams<{ seed?: string }>();

  // --- Состояние публичного режима (одиночный ввод) ---
  const [sourceInput, setSourceInput] = useState<string>('');
  const [singleInputError, setSingleInputError] = useState<string>('');
  const [qrError, setQrError] = useState<string>('');

  // --- Локальные аккаунты в памяти (для незалогиненных) ---
  const [localAccounts, setLocalAccounts] = useState<LocalTwoFaAccount[]>([]);
  const [localSearchInput, setLocalSearchInput] = useState<string>('');
  const [localBulkInput, setLocalBulkInput] = useState<string>('');
  const [localBulkLoading, setLocalBulkLoading] = useState<boolean>(false);
  const [localBulkErrors, setLocalBulkErrors] = useState<{ line: number; reason: string; input: string }[]>([]);
  const [localBulkStats, setLocalBulkStats] = useState<{ added: number; skipped: number } | null>(null);
  const [localTransformStats, setLocalTransformStats] = useState<{ replaced: number; unchanged: number } | null>(null);
  const [localCopyMap, setLocalCopyMap] = useState<Record<string, string>>({});
  const [localNote, setLocalNote] = useState<string>('');
  const [bulkExpanded, setBulkExpanded] = useState<boolean>(true);

  // --- UI-состояние ---
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    try {
      const savedTheme = localStorage.getItem('2fa-theme');
      if (savedTheme === 'dark' || savedTheme === 'light') return savedTheme === 'dark';
      return window.matchMedia?.('(prefers-color-scheme: dark)').matches || false;
    } catch {
      return false;
    }
  });
  const [helpOpen, setHelpOpen] = useState<boolean>(false);
  const portalTopbarRef = useRef<HTMLElement | null>(null);

  // --- Массовые действия ---
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectedLocalIds, setSelectedLocalIds] = useState<Set<string>>(new Set());
  const [serverExportFormat, setServerExportFormat] = useState<AccountExportFormat>('links');
  const [localExportFormat, setLocalExportFormat] = useState<AccountExportFormat>('links');

  // --- Пагинация ---
  const [perPage, setPerPage] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [localCurrentPage, setLocalCurrentPage] = useState<number>(1);

  // --- Тогл сохранения для массового ввода ---
  const [bulkSaveToProfile, setBulkSaveToProfile] = useState<boolean>(true);

  // --- Раскрываемые строки (expandable rows) ---
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // --- Статус копирования live-кода ---
  const [liveCopyMsg, setLiveCopyMsg] = useState<string>('');
  const [managerLiveCopyMsg, setManagerLiveCopyMsg] = useState<string>('');

  // --- Авторизация ---
  const [authChecking, setAuthChecking] = useState<boolean>(true);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [authUserId, setAuthUserId] = useState<string>('');
  const lastTrackedPublicSecretRef = useRef<string | null>(null);
  const lastTrackedManagerSecretRef = useRef<string | null>(null);

  // Счётчики статистики уходят пачкой раз в ~1,5 с (см. codeTelemetry): только числа.
  const isAuthorizedRef = useRef(false);
  isAuthorizedRef.current = isAuthorized;
  useEffect(() => configureCodeTelemetry((payload, keepalive) => {
    const token = isAuthorizedRef.current ? getStoredToken() : '';
    void fetch(`${getTwoFaApiBaseUrl()}/activity/code`, {
      method: 'POST',
      headers: token
        ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
        : { 'Content-Type': 'application/json' },
      credentials: 'include',
      referrerPolicy: 'no-referrer',
      keepalive,
      body: JSON.stringify(payload),
    }).catch(() => {});
  }), []);

  const recordSecretInput = useCallback((units: number) => {
    noteSecretsEntered(units);
  }, []);

  // --- Менеджер аккаунтов (залогиненные) ---
  const [accounts, setAccounts] = useState<TwoFaAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState<boolean>(false);
  const [accountsError, setAccountsError] = useState<string>('');

  const [bulkInput, setBulkInput] = useState<string>('');
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  const [bulkResult, setBulkResult] = useState<BulkResponse | null>(null);
  const [bulkTransformStats, setBulkTransformStats] = useState<{ replaced: number; unchanged: number } | null>(null);

  const [manualNote, setManualNote] = useState<string>('');
  const [manualSource, setManualSource] = useState<string>('');
  const [manualLoading, setManualLoading] = useState<boolean>(false);
  const [manualError, setManualError] = useState<string>('');

  const [searchInput, setSearchInput] = useState<string>('');
  // expandedNotes удалён — в новом UI заметки отображаются в строке
  const [managerMessage, setManagerMessage] = useState<string>('');
  const [managerCopyMap, setManagerCopyMap] = useState<Record<number, string>>({});
  const [tickTime, setTickTime] = useState<number>(Date.now());
  const timeoutIdsRef = useRef<Set<number>>(new Set());

  const scheduleTimeout = useCallback((callback: () => void, delay: number) => {
    const timeoutId = window.setTimeout(() => {
      timeoutIdsRef.current.delete(timeoutId);
      callback();
    }, delay);
    timeoutIdsRef.current.add(timeoutId);
  }, []);

  useEffect(() => () => {
    for (const timeoutId of Array.from(timeoutIdsRef.current)) {
      window.clearTimeout(timeoutId);
    }
    timeoutIdsRef.current.clear();
  }, []);

  const showManagerMessage = useCallback((message: string) => {
    setManagerMessage(message);
    scheduleTimeout(() => {
      setManagerMessage('');
    }, 2000);
  }, [scheduleTimeout]);

  const showAccountCopyStatus = useCallback((accountId: number, message: string) => {
    setManagerCopyMap((previous) => ({
      ...previous,
      [accountId]: message,
    }));

    scheduleTimeout(() => {
      setManagerCopyMap((previous) => {
        const next = { ...previous };
        delete next[accountId];
        return next;
      });
    }, 1600);
  }, [scheduleTimeout]);

  const showLocalCopyStatus = useCallback((localId: string, message: string) => {
    setLocalCopyMap((previous) => ({ ...previous, [localId]: message }));
    scheduleTimeout(() => {
      setLocalCopyMap((previous) => {
        const next = { ...previous };
        delete next[localId];
        return next;
      });
    }, 1600);
  }, [scheduleTimeout]);

  const handleLiveCodeCopy = useCallback(async (code: string, variant: 'public' | 'manager', source?: { secret: string; period: number } | null) => {
    if (!code || code.includes('-')) return;
    const ok = await copyTextToClipboard(code);
    if (ok && source) noteCodesObtained([source]);
    const msg = ok ? 'Скопировано' : 'Ошибка';
    if (variant === 'public') {
      setLiveCopyMsg(msg);
      scheduleTimeout(() => setLiveCopyMsg(''), 1600);
    } else {
      setManagerLiveCopyMsg(msg);
      scheduleTimeout(() => setManagerLiveCopyMsg(''), 1600);
    }
  }, [scheduleTimeout]);

  const loadUserAccounts = useCallback(async () => {
    if (!isAuthorized) {
      return;
    }

    const token = getStoredToken();
    if (!token) {
      setIsAuthorized(false);
      setAuthUserId('');
      setAccounts([]);
      return;
    }

    setAccountsLoading(true);
    setAccountsError('');

    try {
      const response = await fetch(`${getTwoFaApiBaseUrl()}/accounts`, {
        method: 'GET',
        headers: buildAuthHeaders(token, false),
        credentials: 'include',
      });

      const payload = await safeReadJson(response);

      if (!response.ok || !payload?.success) {
        throw new Error(
          typeof payload?.error === 'string'
            ? payload.error
            : 'Не удалось получить список аккаунтов',
        );
      }

      const list = Array.isArray(payload?.data?.accounts)
        ? payload.data.accounts
        : [];

      setAccounts(list.map(normalizeAccountFromApi));
    } catch (err) {
      setAccountsError(err instanceof Error ? err.message : 'Не удалось получить список аккаунтов');
    } finally {
      setAccountsLoading(false);
    }
  }, [isAuthorized]);

  // Тик каждую секунду для обновления TOTP-кодов
  useEffect(() => {
    let timeoutId: number;
    let cancelled = false;

    const updateTick = () => {
      if (cancelled) return;
      setTickTime(Date.now());
      timeoutId = window.setTimeout(updateTick, 1000);
    };

    timeoutId = window.setTimeout(updateTick, 1000);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, []);

  // Обработка URL-параметра seed для публичного и авторизованного режима.
  useEffect(() => {
    if (authChecking || typeof seed !== 'string') {
      return;
    }

    const decodedSeed = safeDecode(seed);
    if (isAuthorized) {
      setManualSource(decodedSeed);
    } else {
      setSourceInput(decodedSeed);
    }
    setQrError('');
    setSingleInputError('');
  }, [authChecking, isAuthorized, seed]);

  // Проверка авторизации (с автообновлением истёкшего токена)
  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();

    const verifyAuthorization = async () => {
      setAuthChecking(true);

      let token = getStoredToken();
      if (!token) {
        if (!cancelled) {
          setIsAuthorized(false);
          setAuthUserId('');
          setAuthChecking(false);
        }
        return;
      }

      try {
        let response = await fetch(getAuthVerifyUrl(), {
          method: 'GET',
          headers: buildAuthHeaders(token, false),
          credentials: 'include',
          signal: abortController.signal,
        });

        // Если токен истёк — пробуем обновить через refresh-token
        if (response.status === 401) {
          const refreshToken = getStoredRefreshToken();
          if (refreshToken) {
            const newToken = await tryRefreshToken(refreshToken);
            if (newToken) {
              token = newToken;
              response = await fetch(getAuthVerifyUrl(), {
                method: 'GET',
                headers: buildAuthHeaders(token, false),
                credentials: 'include',
                signal: abortController.signal,
              });
            }
          }
        }

        const payload = await safeReadJson(response);
        const user = payload?.data?.user;
        const userId = user?.id;

        if (!response.ok || userId === undefined || userId === null || userId === '') {
          throw new Error('Токен невалиден');
        }

        if (!cancelled) {
          setIsAuthorized(true);
          setAuthUserId(String(userId));
        }
      } catch {
        if (!cancelled) {
          setIsAuthorized(false);
          setAuthUserId('');
        }
      } finally {
        if (!cancelled) {
          setAuthChecking(false);
        }
      }
    };

    verifyAuthorization();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, []);

  // Загрузка аккаунтов при авторизации
  useEffect(() => {
    if (!isAuthorized) {
      setAccounts([]);
      setBulkResult(null);
      setBulkInput('');
      setManualError('');
      setManualNote('');
      setManualSource('');
      setSearchInput('');
      setAccountsError('');
      return;
    }

    loadUserAccounts();
  }, [isAuthorized, loadUserAccounts]);

  // Runtime-аккаунты менеджера (с живым кодом)
  const runtimeAccounts = useMemo(() => {
    return accounts.map((account) => buildRuntimeAccount(account, tickTime));
  }, [accounts, tickTime]);

  const filteredRuntimeAccounts = useMemo(() => {
    const needle = searchInput.trim().toLowerCase();

    if (!needle) {
      return runtimeAccounts;
    }

    return runtimeAccounts.filter((account) => account.search_blob.includes(needle));
  }, [runtimeAccounts, searchInput]);

  // Runtime-аккаунты для публичного режима (с живым кодом)
  const runtimeLocalAccounts = useMemo(() => {
    return localAccounts.map((account) => buildRuntimeLocalAccount(account, tickTime));
  }, [localAccounts, tickTime]);

  const filteredLocalAccounts = useMemo(() => {
    const needle = localSearchInput.trim().toLowerCase();
    if (!needle) {
      return runtimeLocalAccounts;
    }
    return runtimeLocalAccounts.filter((account) => account.search_blob.includes(needle));
  }, [runtimeLocalAccounts, localSearchInput]);

  // Пагинация для залогиненных
  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredRuntimeAccounts.length / perPage)), [filteredRuntimeAccounts.length, perPage]);
  const paginatedAccounts = useMemo(() => {
    const page = Math.min(currentPage, totalPages);
    const start = (page - 1) * perPage;
    return filteredRuntimeAccounts.slice(start, start + perPage);
  }, [filteredRuntimeAccounts, currentPage, perPage, totalPages]);

  // Пагинация для локальных
  const localTotalPages = useMemo(() => Math.max(1, Math.ceil(filteredLocalAccounts.length / perPage)), [filteredLocalAccounts.length, perPage]);
  const paginatedLocalAccounts = useMemo(() => {
    const page = Math.min(localCurrentPage, localTotalPages);
    const start = (page - 1) * perPage;
    return filteredLocalAccounts.slice(start, start + perPage);
  }, [filteredLocalAccounts, localCurrentPage, perPage, localTotalPages]);

  // Живое превью кода для текущего значения sourceInput (с гибким парсингом)
  const liveParsed = useMemo(() => {
    const trimmed = sourceInput.trim();
    if (!trimmed) return null;
    try {
      return parseTotpSource(trimmed);
    } catch {
      // Гибкий поиск: извлекаем Base32 из произвольного текста (с учётом URL)
      try {
        const urlRanges: { s: number; e: number }[] = [];
        const urlPattern = /https?:\/\/[^\s\t]+/gi;
        let um: RegExpExecArray | null;
        while ((um = urlPattern.exec(trimmed)) !== null) {
          urlRanges.push({ s: um.index, e: um.index + um[0].length });
        }
        const matches = Array.from(trimmed.matchAll(/[A-Z2-7]{16,}/gi));
        matches.sort((a, b) => {
          const aUrl = urlRanges.some((r) => (a.index ?? 0) >= r.s && (a.index ?? 0) < r.e);
          const bUrl = urlRanges.some((r) => (b.index ?? 0) >= r.s && (b.index ?? 0) < r.e);
          if (aUrl !== bUrl) return aUrl ? 1 : -1;
          return b[0].length - a[0].length;
        });
        for (const m of matches) {
          try { return parseTotpSource(normalizeBase32Secret(m[0])); } catch { /* далее */ }
        }
      } catch { /* нет совпадений */ }
      return null;
    }
  }, [sourceInput]);

  const liveCode = useMemo(() => {
    if (!liveParsed) return '------';
    try {
      return liveParsed.totp.generate();
    } catch {
      return '------';
    }
  // tickTime нужен для пересчёта каждую секунду
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveParsed, tickTime]);

  const liveRemaining = useMemo(() => {
    if (!liveParsed) return 0;
    const now = Math.floor(Date.now() / 1000);
    return liveParsed.period - (now % liveParsed.period);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveParsed, tickTime]);

  // Живое превью для залогиненного режима (manualSource, с гибким парсингом)
  const manualParsed = useMemo(() => {
    const trimmed = manualSource.trim();
    if (!trimmed) return null;
    try {
      return parseTotpSource(trimmed);
    } catch {
      try {
        const urlRanges: { s: number; e: number }[] = [];
        const urlPattern = /https?:\/\/[^\s\t]+/gi;
        let um: RegExpExecArray | null;
        while ((um = urlPattern.exec(trimmed)) !== null) {
          urlRanges.push({ s: um.index, e: um.index + um[0].length });
        }
        const matches = Array.from(trimmed.matchAll(/[A-Z2-7]{16,}/gi));
        matches.sort((a, b) => {
          const aUrl = urlRanges.some((r) => (a.index ?? 0) >= r.s && (a.index ?? 0) < r.e);
          const bUrl = urlRanges.some((r) => (b.index ?? 0) >= r.s && (b.index ?? 0) < r.e);
          if (aUrl !== bUrl) return aUrl ? 1 : -1;
          return b[0].length - a[0].length;
        });
        for (const m of matches) {
          try { return parseTotpSource(normalizeBase32Secret(m[0])); } catch { /* далее */ }
        }
      } catch { /* нет совпадений */ }
      return null;
    }
  }, [manualSource]);

  const manualLiveCode = useMemo(() => {
    if (!manualParsed) return '------';
    try { return manualParsed.totp.generate(); } catch { return '------'; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualParsed, tickTime]);

  useEffect(() => {
    if (!sourceInput.trim()) {
      lastTrackedPublicSecretRef.current = null;
      return undefined;
    }
    if (authChecking || !liveParsed || liveCode.includes('-')) return undefined;
    const secret = liveParsed.secretBase32;
    if (lastTrackedPublicSecretRef.current === secret) return undefined;
    const timeout = window.setTimeout(() => {
      if (lastTrackedPublicSecretRef.current === secret) return;
      lastTrackedPublicSecretRef.current = secret;
      recordSecretInput(1);
      noteCodesObtained([{ secret, period: liveParsed.period }]);
    }, 700);
    return () => window.clearTimeout(timeout);
  }, [sourceInput, liveParsed, liveCode, authChecking, recordSecretInput]);

  useEffect(() => {
    if (!manualSource.trim()) {
      lastTrackedManagerSecretRef.current = null;
      return undefined;
    }
    if (authChecking || !isAuthorized || !manualParsed || manualLiveCode.includes('-')) return undefined;
    const secret = manualParsed.secretBase32;
    if (lastTrackedManagerSecretRef.current === secret) return undefined;
    const timeout = window.setTimeout(() => {
      if (lastTrackedManagerSecretRef.current === secret) return;
      lastTrackedManagerSecretRef.current = secret;
      recordSecretInput(1);
      noteCodesObtained([{ secret, period: manualParsed.period }]);
    }, 700);
    return () => window.clearTimeout(timeout);
  }, [manualSource, manualParsed, manualLiveCode, authChecking, isAuthorized, recordSecretInput]);

  const manualLiveRemaining = useMemo(() => {
    if (!manualParsed) return 0;
    const now = Math.floor(Date.now() / 1000);
    return manualParsed.period - (now % manualParsed.period);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualParsed, tickTime]);

  useEffect(() => {
    if (authChecking) return;
    const activeSecret = isAuthorized
      ? manualParsed?.secretBase32 || null
      : liveParsed?.secretBase32 || null;
    replaceAddressBarSecret(activeSecret);
  }, [authChecking, isAuthorized, liveParsed, manualParsed]);

  // Тёмная тема: единый контракт темы с SMS, Proxy и portal-topbar.
  useEffect(() => {
    const nextTheme = darkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', nextTheme);
    document.documentElement.setAttribute('data-ui-theme', nextTheme);
    document.documentElement.style.colorScheme = nextTheme;
    try { localStorage.setItem('2fa-theme', darkMode ? 'dark' : 'light'); } catch { /* no-op */ }
  }, [darkMode]);

  useEffect(() => {
    const portalTopbar = portalTopbarRef.current;
    if (!portalTopbar) return undefined;

    const handleThemeRequest = () => setDarkMode((value) => !value);
    portalTopbar.addEventListener('portal-theme-toggle', handleThemeRequest);

    return () => portalTopbar.removeEventListener('portal-theme-toggle', handleThemeRequest);
  }, []);

  // --- Обработчик добавления одного кода в локальный список ---
  const handleAddSingle = useCallback(() => {
    const trimmed = sourceInput.trim();
    if (!trimmed) {
      return;
    }

    try {
      // Сначала прямой разбор, затем гибкий
      let newAccount: LocalTwoFaAccount;
      try {
        const parsed = parseTotpSource(trimmed);
        const noteTrimmed = localNote.trim();
        const displayName = noteTrimmed || buildLocalDisplayName(parsed.issuer, parsed.label, localAccounts.length + 1);
        newAccount = {
          localId: generateLocalId(),
          secretBase32: parsed.secretBase32,
          issuer: parsed.issuer,
          label: parsed.label,
          algorithm: parsed.algorithm,
          digits: parsed.digits,
          period: parsed.period,
          displayName,
          sourceRaw: trimmed,
          note: noteTrimmed || undefined,
        };
      } catch {
        const flexParsed = parseFlexibleLineClient(trimmed, localAccounts.length + 1);
        const noteTrimmed = localNote.trim();
        if (noteTrimmed) flexParsed.displayName = noteTrimmed;
        if (noteTrimmed) flexParsed.note = noteTrimmed;
        newAccount = flexParsed;
      }

      // Проверка дубликатов
      const alreadyExists = localAccounts.some((a) => a.secretBase32 === newAccount.secretBase32)
        || accounts.some((a) => a.secret_base32 === newAccount.secretBase32);
      if (alreadyExists) {
        setSingleInputError('Этот секрет уже добавлен');
        return;
      }

      if (lastTrackedPublicSecretRef.current !== newAccount.secretBase32) {
        lastTrackedPublicSecretRef.current = newAccount.secretBase32;
        recordSecretInput(1);
      }
      noteCodesObtained([{ secret: newAccount.secretBase32, period: newAccount.period }]);
      setLocalAccounts((prev) => [newAccount, ...prev]);
      setSourceInput('');
      setLocalNote('');
      setSingleInputError('');
      setQrError('');
    } catch (err) {
      setSingleInputError(err instanceof Error ? err.message : 'Некорректный ввод');
    }
  }, [sourceInput, localAccounts, localNote, accounts, recordSecretInput]);

  const handleSingleInputChange = (value: string) => {
    setSourceInput(value);
    setSingleInputError('');
    setQrError('');
  };

  const handleSingleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSingle();
    }
  };

  const handleQrUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setQrError('');
    setSingleInputError('');

    try {
      const qrText = await readQrTextFromFile(file);

      if (!/^otpauth:\/\//i.test(qrText)) {
        throw new Error('QR не содержит otpauth://');
      }

      setSourceInput(qrText);
    } catch (err) {
      setQrError(err instanceof Error ? err.message : 'Не удалось распознать QR');
    } finally {
      event.target.value = '';
    }
  };

  const handleQrUploadManager = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setManualError('');
    try {
      const qrText = await readQrTextFromFile(file);
      if (!/^otpauth:\/\//i.test(qrText)) throw new Error('QR не содержит otpauth://');
      setManualSource(qrText);
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Не удалось распознать QR');
    } finally {
      event.target.value = '';
    }
  };

  const handleManualSourceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); handleManualSubmit(); }
  };

  // --- Обработчик массового добавления в локальный список ---
  const handleLocalBulkSubmit = useCallback(() => {
    const text = localBulkInput.trim();
    if (!text) return;

    setLocalBulkLoading(true);
    setLocalBulkErrors([]);
    setLocalBulkStats(null);

    const existingSecrets = new Set([
      ...localAccounts.map((a) => a.secretBase32),
      ...accounts.map((a) => a.secret_base32),
    ]);

    const lines = text.split(/\r?\n/);
    const newAccounts: LocalTwoFaAccount[] = [];
    const errors: { line: number; reason: string; input: string }[] = [];
    let lineCounter = localAccounts.length + 1;

    for (let i = 0; i < lines.length; i += 1) {
      const rawLine = lines[i];
      if (!rawLine || !rawLine.trim()) continue;

      try {
        const parsed = parseFlexibleLineClient(rawLine, lineCounter);
        if (existingSecrets.has(parsed.secretBase32)) {
          errors.push({ line: i + 1, reason: 'Дубликат — секрет уже добавлен', input: rawLine });
          continue;
        }
        existingSecrets.add(parsed.secretBase32);
        newAccounts.push(parsed);
        lineCounter += 1;
      } catch (err) {
        errors.push({
          line: i + 1,
          reason: err instanceof Error ? err.message : 'Ошибка разбора',
          input: rawLine,
        });
      }
    }

    setLocalAccounts((prev) => [...newAccounts, ...prev]);
    if (newAccounts.length > 0) {
      recordSecretInput(newAccounts.length);
      noteCodesObtained(newAccounts.map((a) => ({ secret: a.secretBase32, period: a.period })));
    }
    setLocalBulkErrors(errors);
    setLocalBulkStats({ added: newAccounts.length, skipped: errors.length });
    setLocalBulkInput('');
    setLocalBulkLoading(false);
  }, [localBulkInput, localAccounts, accounts, recordSecretInput]);

  const handleTransformLocalBulk = useCallback(() => {
    if (!localBulkInput.trim()) return;
    const result = transformBulkInputToPermanentLinks(localBulkInput);
    setLocalBulkInput(result.text);
    setLocalTransformStats({ replaced: result.replaced, unchanged: result.unchanged });
  }, [localBulkInput]);

  const handleTransformBulk = useCallback(() => {
    if (!bulkInput.trim()) return;
    const result = transformBulkInputToPermanentLinks(bulkInput);
    setBulkInput(result.text);
    setBulkTransformStats({ replaced: result.replaced, unchanged: result.unchanged });
  }, [bulkInput]);

  const handleDeleteLocalAccount = useCallback((localId: string) => {
    const account = localAccounts.find((item) => item.localId === localId);
    const accountName = account?.displayName || 'этот аккаунт';
    if (!window.confirm(`Удалить «${accountName}» из текущего списка?`)) return;

    setLocalAccounts((prev) => prev.filter((a) => a.localId !== localId));
    setSelectedLocalIds((prev) => {
      const next = new Set(prev);
      next.delete(localId);
      return next;
    });
  }, [localAccounts]);

  const handleLocalCodeCopy = useCallback(async (localId: string, code: string) => {
    if (!code || code.includes('-')) return;
    const copied = await copyTextToClipboard(code);
    const account = localAccounts.find((item) => item.localId === localId);
    if (copied && account) noteCodesObtained([{ secret: account.secretBase32, period: account.period }]);
    showLocalCopyStatus(localId, copied ? 'Скопировано' : 'Не удалось скопировать');
  }, [showLocalCopyStatus, localAccounts]);

  // --- Обработчики менеджера (залогиненные) ---
  const handleBulkSubmit = async () => {
    const token = getStoredToken();

    if (!token) {
      setIsAuthorized(false);
      setAuthUserId('');
      return;
    }

    const payloadText = bulkInput.trim();

    if (!payloadText) {
      showManagerMessage('Введите хотя бы одну строку для bulk-добавления');
      return;
    }

    setBulkLoading(true);
    setBulkResult(null);

    try {
      const response = await fetch(`${getTwoFaApiBaseUrl()}/accounts/bulk`, {
        method: 'POST',
        headers: buildAuthHeaders(token, true),
        credentials: 'include',
        body: JSON.stringify({ text: payloadText }),
      });

      const payload = await safeReadJson(response);

      if (!response.ok || !payload?.success) {
        throw new Error(
          typeof payload?.error === 'string'
            ? payload.error
            : 'Не удалось добавить аккаунты списком',
        );
      }

      const result: BulkResponse = {
        created: Array.isArray(payload?.data?.created)
          ? payload.data.created.map((item: any) => ({
            line: Number(item?.line) || 0,
            account: normalizeAccountFromApi(item?.account),
          }))
          : [],
        skipped: Array.isArray(payload?.data?.skipped)
          ? payload.data.skipped.map((item: any) => ({
            line: Number(item?.line) || 0,
            reason: typeof item?.reason === 'string' ? item.reason : 'Пропущено',
            input: typeof item?.input === 'string' ? item.input : '',
          }))
          : [],
        errors: Array.isArray(payload?.data?.errors)
          ? payload.data.errors.map((item: any) => ({
            line: Number(item?.line) || 0,
            reason: typeof item?.reason === 'string' ? item.reason : 'Ошибка',
            input: typeof item?.input === 'string' ? item.input : '',
          }))
          : [],
      };

      setBulkResult(result);
      setBulkInput('');
      showManagerMessage(`Добавлено: ${result.created.length}, пропущено: ${result.skipped.length}, ошибок: ${result.errors.length}`);
      await loadUserAccounts();
    } catch (err) {
      showManagerMessage(err instanceof Error ? err.message : 'Не удалось добавить аккаунты списком');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleManualSubmit = async () => {
    const token = getStoredToken();

    if (!token) {
      setIsAuthorized(false);
      setAuthUserId('');
      return;
    }

    if (!manualSource.trim()) {
      setManualError('Введите 2FA строку/секрет');
      return;
    }

    setManualLoading(true);
    setManualError('');

    try {
      const response = await fetch(`${getTwoFaApiBaseUrl()}/accounts`, {
        method: 'POST',
        headers: buildAuthHeaders(token, true),
        credentials: 'include',
        body: JSON.stringify({
          account_note: manualNote,
          source: manualSource,
        }),
      });

      const payload = await safeReadJson(response);

      if (!response.ok || !payload?.success) {
        throw new Error(
          typeof payload?.error === 'string'
            ? payload.error
            : 'Не удалось добавить аккаунт',
        );
      }

      setManualNote('');
      setManualSource('');
      setManualError('');
      showManagerMessage('Аккаунт добавлен');
      await loadUserAccounts();
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Не удалось добавить аккаунт');
    } finally {
      setManualLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId: number) => {
    const account = accounts.find((item) => item.id === accountId);
    const accountName = account ? buildDisplayName(account) : 'этот аккаунт';
    if (!window.confirm(`Удалить «${accountName}» без возможности восстановления?`)) return;

    const token = getStoredToken();

    if (!token) {
      setIsAuthorized(false);
      setAuthUserId('');
      return;
    }

    try {
      const response = await fetch(`${getTwoFaApiBaseUrl()}/accounts/${accountId}`, {
        method: 'DELETE',
        headers: buildAuthHeaders(token, false),
        credentials: 'include',
      });

      const payload = await safeReadJson(response);

      if (!response.ok || !payload?.success) {
        throw new Error(
          typeof payload?.error === 'string'
            ? payload.error
            : 'Не удалось удалить аккаунт',
        );
      }

      setAccounts((previous) => previous.filter((item) => item.id !== accountId));
      setSelectedIds((previous) => {
        const next = new Set(previous);
        next.delete(accountId);
        return next;
      });
      showManagerMessage('Аккаунт удален');
    } catch (err) {
      showManagerMessage(err instanceof Error ? err.message : 'Не удалось удалить аккаунт');
    }
  };

  const handleMassDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Удалить выбранные аккаунты (${selectedIds.size}) без возможности восстановления?`)) return;
    const token = getStoredToken();
    if (!token) { setIsAuthorized(false); setAuthUserId(''); return; }
    const ids = Array.from(selectedIds);
    const deletedIds = new Set<number>();
    for (const id of ids) {
      try {
        const response = await fetch(`${getTwoFaApiBaseUrl()}/accounts/${id}`, {
          method: 'DELETE', headers: buildAuthHeaders(token, false), credentials: 'include',
        });
        const p = await safeReadJson(response);
        if (response.ok && p?.success) { deletedIds.add(id); }
      } catch { /* продолжаем удаление остальных */ }
    }
    setAccounts((prev) => prev.filter((account) => !deletedIds.has(account.id)));
    setSelectedIds((previous) => new Set(
      Array.from(previous).filter((id) => !deletedIds.has(id)),
    ));
    showManagerMessage(`Удалено: ${deletedIds.size} из ${ids.length}`);
  };

  const handleMassDeleteLocal = () => {
    if (selectedLocalIds.size === 0) return;
    if (!window.confirm(`Удалить выбранные аккаунты из текущего списка (${selectedLocalIds.size})?`)) return;
    setLocalAccounts((prev) => prev.filter((a) => !selectedLocalIds.has(a.localId)));
    const count = selectedLocalIds.size;
    setSelectedLocalIds(new Set());
    showManagerMessage(`Удалено: ${count}`);
  };

  const toggleSelectAccount = (id: number) => {
    setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const toggleSelectLocalAccount = (id: string) => {
    setSelectedLocalIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const toggleExpandRow = (key: string) => {
    setExpandedRows((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };

  const handleExportLocal = useCallback(() => {
    const items = localAccounts
      .filter((account) => selectedLocalIds.has(account.localId))
      .map((account) => ({
        secretBase32: account.secretBase32,
        sourceRaw: account.sourceRaw,
        displayName: account.displayName,
      }));
    if (items.length === 0) return;
    downloadTextFile(buildAccountExportText(items, localExportFormat), exportFilename(localExportFormat));
  }, [localAccounts, localExportFormat, selectedLocalIds]);

  const handleExportServer = useCallback(() => {
    const items = accounts
      .filter((account) => selectedIds.has(account.id))
      .map((account) => ({
        secretBase32: account.secret_base32,
        sourceRaw: account.source_raw,
        displayName: buildDisplayName(account),
      }));
    if (items.length === 0) return;
    downloadTextFile(buildAccountExportText(items, serverExportFormat), exportFilename(serverExportFormat));
  }, [accounts, selectedIds, serverExportFormat]);

  // Массовый ввод с тоглом сохранения (для залогиненных)
  const handleBulkSubmitWithToggle = async () => {
    if (bulkSaveToProfile) {
      await handleBulkSubmit();
    } else {
      const text = bulkInput.trim();
      if (!text) return;
      const existingSecrets = new Set([
        ...localAccounts.map((a) => a.secretBase32),
        ...accounts.map((a) => a.secret_base32),
      ]);
      const lines = text.split(/\r?\n/);
      const newAccs: LocalTwoFaAccount[] = [];
      let c = localAccounts.length + 1;
      for (const rawLine of lines) {
        if (!rawLine.trim()) continue;
        try {
          const acc = parseFlexibleLineClient(rawLine, c);
          if (existingSecrets.has(acc.secretBase32)) continue;
          existingSecrets.add(acc.secretBase32);
          acc.isMinimal = true;
          newAccs.push(acc);
          c += 1;
        } catch { /* пропускаем */ }
      }
      setLocalAccounts((prev) => [...newAccs, ...prev]);
      if (newAccs.length > 0) {
        recordSecretInput(newAccs.length);
        noteCodesObtained(newAccs.map((a) => ({ secret: a.secretBase32, period: a.period })));
      }
      setBulkInput('');
      showManagerMessage(`Добавлено локально: ${newAccs.length}`);
    }
  };

  const handleRuntimeCodeCopy = async (accountId: number, currentCodeValue: string) => {
    if (!currentCodeValue || currentCodeValue.includes('-')) {
      return;
    }

    const copied = await copyTextToClipboard(currentCodeValue);

    if (copied) {
      const account = accounts.find((item) => item.id === accountId);
      if (account) noteCodesObtained([{ secret: account.secret_base32, period: account.period }]);
      showAccountCopyStatus(accountId, 'Скопировано');
      return;
    }

    showAccountCopyStatus(accountId, 'Не удалось скопировать код');
  };

  // =============================================
  // ПУБЛИЧНЫЙ ИНТЕРФЕЙС (незалогиненные)
  // =============================================

  const avatarColor = (name: string) => {
    const colors = ['#2563eb','#7c3aed','#db2777','#ea580c','#0891b2','#059669','#4f46e5','#be123c'];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return colors[Math.abs(h) % colors.length];
  };

  const publicMain = (
    <>
      <aside className="fa-privacy-note">
        <AppIcon name="shield" />
        <div>
          <strong>Ваш секрет остаётся на устройстве</strong>
          <span>
            В гостевом режиме данные обрабатываются только в браузере. Чтобы хранить аккаунты в профиле,
            {' '}<a href="/login">войдите</a> или <a href="/register">зарегистрируйтесь</a>.
          </span>
        </div>
      </aside>

      {/* БЛОК A: Поле ввода */}
      <section className="fa-card fa-input-section" aria-labelledby="public-input-title">
        <div className="fa-section-head">
          <div>
            <h2 id="public-input-title">Получить одноразовый код</h2>
            <p id="public-input-description">Вставьте секрет, ссылку <span className="mono">otpauth://</span> или строку с данными аккаунта.</p>
          </div>
          <span className="fa-format-note">Base32 · URI · QR</span>
        </div>
        <div className={`fa-primary-field ${singleInputError || qrError ? 'has-error' : ''}`}>
          <label className="fa-field-label" htmlFor="seed-input">Секрет или ссылка 2FA</label>
          <div className="fa-input-row">
            <div className="fa-input-wrap">
              <input
                id="seed-input"
                name="totp-source"
                className="fa-input-main mono"
                type="text"
                autoComplete="off"
                spellCheck={false}
                aria-invalid={Boolean(singleInputError || qrError)}
                aria-describedby={`public-input-description${singleInputError ? ' seed-input-error' : ''}${qrError ? ' seed-qr-error' : ''}`}
                placeholder="Например, JBSWY3DPEHPK3PXP или otpauth://…"
                value={sourceInput}
                onChange={(event) => handleSingleInputChange(event.target.value)}
                onKeyDown={handleSingleInputKeyDown}
              />
              <button
                type="button"
                className="fa-btn-qr"
                onClick={() => document.getElementById('qr-upload')?.click()}
                title="Загрузить QR-код"
                aria-label="Загрузить QR-код"
              >
                <AppIcon name="camera" />
              </button>
              <input
                id="qr-upload"
                name="totp-qr"
                aria-label="Файл с QR-кодом"
                className="fa-hidden-file"
                type="file"
                tabIndex={-1}
                accept="image/png,image/jpeg,image/webp"
                onChange={handleQrUpload}
              />
            </div>
            <button
              type="button"
              className="fa-btn-add"
              onClick={handleAddSingle}
              disabled={!sourceInput.trim() || !liveParsed}
            >
              Добавить
            </button>
          </div>
          {singleInputError ? <div id="seed-input-error" className="fa-error-box" role="alert">{singleInputError}</div> : null}
          {qrError ? <div id="seed-qr-error" className="fa-error-box" role="alert">{qrError}</div> : null}
        </div>

        <div className="fa-note-row">
          <label className="fa-field-label" htmlFor="local-note">Название аккаунта</label>
          <input
            id="local-note"
            name="account-note"
            className="fa-note-input"
            type="text"
            autoComplete="off"
            placeholder="Например, рабочая почта…"
            value={localNote}
            onChange={(event) => setLocalNote(event.target.value)}
          />
        </div>

        <div className="fa-input-sub">
          <button
            type="button"
            className="fa-help-toggle"
            aria-expanded={helpOpen}
            aria-controls="public-input-help"
            onClick={() => setHelpOpen((v) => !v)}
          >
            Что можно вставить
            <AppIcon name="chevron-down" className={helpOpen ? 'is-rotated' : ''} />
          </button>
        </div>

        <div id="public-input-help" className={`fa-help-panel ${helpOpen ? 'is-open' : ''}`}>
          <div className="fa-help-title">Поддерживаемые форматы</div>
          <div className="fa-help-item">
            <span className="fa-help-tag">Base32</span>
            <span>Секретный ключ, например <code>JBSWY3DPEHPK3PXP</code></span>
          </div>
          <div className="fa-help-item">
            <span className="fa-help-tag">URI</span>
            <span>Ссылка вида <code>otpauth://totp/Service:user?secret=…</code></span>
          </div>
          <div className="fa-help-item">
            <span className="fa-help-tag">Текст</span>
            <span>Произвольный текст, содержащий секрет или URI — он будет извлечён автоматически</span>
          </div>
        </div>
      </section>

      {/* БЛОК B: Карточка живого кода */}
      {liveParsed ? (
        <div className={`fa-live-card ${liveRemaining <= 5 ? 'is-urgent' : ''}`}>
          <div className="fa-live-top">
            <span className="fa-live-service">{liveParsed.issuer || liveParsed.label || 'TOTP'}</span>
          </div>
          <div className="fa-live-code-row">
            <button type="button" className="fa-live-code is-clickable" onClick={() => handleLiveCodeCopy(liveCode, 'public', liveParsed ? { secret: liveParsed.secretBase32, period: liveParsed.period } : null)} title="Скопировать текущий код" aria-label={`Скопировать код ${liveCode}`}>{liveCode}</button>
            <div className="fa-live-timer">
              <div className="fa-live-timer-num">{liveRemaining}</div>
              <div className="fa-live-timer-label">сек</div>
            </div>
          </div>
          <div className="fa-live-bar">
            <div className="fa-live-bar-fill" style={{ width: `${(liveRemaining / liveParsed.period) * 100}%` }} />
          </div>
          <div className="fa-live-bottom">
            <span className="fa-live-meta">{liveParsed.algorithm} · {liveParsed.digits} цифр · {liveParsed.period}с</span>
            {liveCopyMsg ? (
              <span className="fa-btn-copy-msg" aria-live="polite">{liveCopyMsg}</span>
            ) : (
              <button type="button" className="fa-btn-copy" onClick={() => handleLiveCodeCopy(liveCode, 'public', liveParsed ? { secret: liveParsed.secretBase32, period: liveParsed.period } : null)} disabled={liveCode === '------'}>
                Копировать
              </button>
            )}
          </div>
        </div>
      ) : null}
      {liveParsed ? (
        <SingleSharePanel config={{
          secretBase32: liveParsed.secretBase32,
          issuer: liveParsed.issuer,
          label: liveParsed.label,
          algorithm: liveParsed.algorithm,
          digits: liveParsed.digits,
          period: liveParsed.period,
          fallbackLabel: localNote || '2FA',
        }} />
      ) : null}

      {/* БЛОК C: Массовый ввод */}
      <section className={`fa-bulk-section ${bulkExpanded ? 'is-open' : ''}`}>
        <button
          type="button"
          className="fa-bulk-header"
          aria-expanded={bulkExpanded}
          aria-controls="public-bulk-panel"
          onClick={() => setBulkExpanded((v) => !v)}
        >
          <span>
            <span className="fa-bulk-title">Добавить несколько аккаунтов</span>
            <span className="fa-bulk-summary">По одному секрету или ссылке на строку</span>
          </span>
          <AppIcon name="chevron-down" className="fa-bulk-chevron" />
        </button>
        <div id="public-bulk-panel" className="fa-bulk-body">
          <div className="fa-bulk-hint">
            Поддерживаются <code>otpauth://totp/…</code> URI и <code>Base32</code>-секреты.
          </div>
          <label className="fa-field-label" htmlFor="public-bulk-input">Список аккаунтов</label>
          <textarea
            id="public-bulk-input"
            name="bulk-totp-source"
            className="fa-textarea"
            autoComplete="off"
            spellCheck={false}
            placeholder={'Пример:\ngithub otpauth://totp/…\ntelegram JBSWY3DPEHPK3PXP'}
            value={localBulkInput}
            onChange={(event) => {
              setLocalBulkInput(event.target.value);
              setLocalTransformStats(null);
            }}
          />
          <div className="fa-bulk-transform-row">
            <button
              type="button"
              className="fa-btn-transform"
              onClick={handleTransformLocalBulk}
              disabled={!localBulkInput.trim()}
            >
              Заменить коды ссылками
            </button>
            <span className="fa-transform-help">
              Заменяет Base32, otpauth:// и чужие URL нашей постоянной ссылкой, сохраняя остальной текст строки.
            </span>
            {localTransformStats ? (
              <span className="fa-transform-result">
                Заменено: {localTransformStats.replaced}, без изменений: {localTransformStats.unchanged}
              </span>
            ) : null}
          </div>
          <div className="fa-bulk-footer">
            <button
              type="button"
              className="fa-btn-add"
              onClick={handleLocalBulkSubmit}
              disabled={localBulkLoading || !localBulkInput.trim()}
              style={{ padding: '10px 24px' }}
            >
              {localBulkLoading ? 'Добавление…' : 'Добавить все'}
            </button>
            {localBulkStats ? (
              <span className="fa-bulk-stats">
                Добавлено: <strong>{localBulkStats.added}</strong>, ошибок: <strong>{localBulkStats.skipped}</strong>
              </span>
            ) : null}
          </div>
          {localBulkErrors.length > 0 ? (
            <div className="fa-bulk-errors">
              <div className="fa-bulk-errors-title">Ошибки разбора</div>
              {localBulkErrors.map((item, index) => (
                <div key={`lerr-${index}`} className="fa-bulk-errors-item">
                  строка {item.line}: {item.reason}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {/* БЛОК D: Список добавленных кодов */}
      {localAccounts.length > 0 ? (
        <section className="fa-accs">
          <div className="fa-accs-header">
            <div className="fa-accs-title">
              Добавленные
              <span className="fa-accs-count">{filteredLocalAccounts.length}</span>
            </div>
            <input
              id="public-account-search"
              name="account-search"
              className="fa-search"
              type="search"
              autoComplete="off"
              aria-label="Поиск по добавленным аккаунтам"
              placeholder="Поиск по аккаунтам…"
              value={localSearchInput}
              onChange={(event) => { setLocalSearchInput(event.target.value); setLocalCurrentPage(1); }}
            />
          </div>

          {/* Тулбар массовых действий */}
          <div className="fa-accs-toolbar">
            <label>
              <input
                type="checkbox"
                checked={filteredLocalAccounts.length > 0 && filteredLocalAccounts.every((a) => selectedLocalIds.has(a.localId))}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedLocalIds((prev) => { const s = new Set(prev); filteredLocalAccounts.forEach((a) => s.add(a.localId)); return s; });
                  } else {
                    setSelectedLocalIds((prev) => { const s = new Set(prev); filteredLocalAccounts.forEach((a) => s.delete(a.localId)); return s; });
                  }
                }}
              />
              Выделить все
            </label>
            {selectedLocalIds.size > 0 ? (
              <button type="button" className="fa-btn-mass-del" onClick={handleMassDeleteLocal}>
                Удалить выбранные ({selectedLocalIds.size})
              </button>
            ) : null}
            <div className="fa-toolbar-spacer" />
            <ExportControls
              format={localExportFormat}
              selectedCount={selectedLocalIds.size}
              onFormatChange={setLocalExportFormat}
              onExport={handleExportLocal}
            />
            <div className="fa-per-page">
              <label className="fa-per-page-label" htmlFor="public-per-page">На странице:</label>
              <select id="public-per-page" name="accounts-per-page" value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setLocalCurrentPage(1); }}>
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
              </select>
            </div>
          </div>
          <AccountTableHeader />

          {filteredLocalAccounts.length === 0 ? (
            <div className="fa-accs-empty">Ничего не найдено</div>
          ) : null}

          {paginatedLocalAccounts.map((account) => {
            const isUrgent = account.remaining_seconds > 0 && account.remaining_seconds <= 5;
            const rowKey = `local-${account.localId}`;
            const isExpanded = expandedRows.has(rowKey);
            return (
              <AccountTableRow
                key={account.localId}
                displayName={account.displayName}
                note={account.note}
                secretBase32={account.secretBase32}
                currentCode={account.current_code}
                remainingSeconds={account.remaining_seconds}
                copyStatus={localCopyMap[account.localId]}
                selected={selectedLocalIds.has(account.localId)}
                urgent={isUrgent}
                expanded={isExpanded}
                avatarColor={avatarColor(account.displayName)}
                shareConfig={localShareConfig(account)}
                onSelect={() => toggleSelectLocalAccount(account.localId)}
                onToggle={() => toggleExpandRow(rowKey)}
                onCopyCode={() => handleLocalCodeCopy(account.localId, account.current_code)}
                onDelete={() => handleDeleteLocalAccount(account.localId)}
                details={(
                  <>
                    <div className="fa-acc-detail"><span className="fa-detail-label">Алгоритм:</span> {account.algorithm}</div>
                    <div className="fa-acc-detail"><span className="fa-detail-label">Цифр:</span> {account.digits}</div>
                    <div className="fa-acc-detail"><span className="fa-detail-label">Период:</span> {account.period}с</div>
                    {account.issuer ? <div className="fa-acc-detail"><span className="fa-detail-label">Issuer:</span> {account.issuer}</div> : null}
                    {account.label ? <div className="fa-acc-detail"><span className="fa-detail-label">Label:</span> {account.label}</div> : null}
                    {account.sourceRaw ? <div className="fa-acc-detail"><span className="fa-detail-label">Исходная строка:</span> <span className="mono">{account.sourceRaw}</span></div> : null}
                  </>
                )}
              />
            );
          })}

          {/* Пагинация */}
          {localTotalPages > 1 ? (
            <div className="fa-pagination">
              <span className="fa-pagination-info">
                {((localCurrentPage - 1) * perPage) + 1}–{Math.min(localCurrentPage * perPage, filteredLocalAccounts.length)} из {filteredLocalAccounts.length}
              </span>
              <div className="fa-pagination-controls">
                <button type="button" className="fa-pagination-btn" aria-label="Предыдущая страница" disabled={localCurrentPage <= 1} onClick={() => setLocalCurrentPage((p) => p - 1)}><AppIcon name="arrow-left" /></button>
                {Array.from({ length: Math.min(localTotalPages, 7) }, (_, i) => {
                  let page: number;
                  if (localTotalPages <= 7) { page = i + 1; }
                  else if (localCurrentPage <= 4) { page = i + 1; }
                  else if (localCurrentPage >= localTotalPages - 3) { page = localTotalPages - 6 + i; }
                  else { page = localCurrentPage - 3 + i; }
                  return (
                    <button key={page} type="button" className={`fa-pagination-btn ${page === localCurrentPage ? 'is-active' : ''}`} onClick={() => setLocalCurrentPage(page)}>
                      {page}
                    </button>
                  );
                })}
                <button type="button" className="fa-pagination-btn" aria-label="Следующая страница" disabled={localCurrentPage >= localTotalPages} onClick={() => setLocalCurrentPage((p) => p + 1)}><AppIcon name="arrow-right" /></button>
              </div>
            </div>
          ) : null}
          <div className="fa-table-footer">
            <span className="fa-table-footer-label">Выбрано: {selectedLocalIds.size}</span>
            <ExportControls
              format={localExportFormat}
              selectedCount={selectedLocalIds.size}
              onFormatChange={setLocalExportFormat}
              onExport={handleExportLocal}
            />
          </div>
        </section>
      ) : null}
    </>
  );

  // =============================================
  // МЕНЕДЖЕР АККАУНТОВ (залогиненные)
  // =============================================
  const managerMain = (
    <>
      {accountsError ? <div className="fa-error-box fa-page-message" role="alert">{accountsError}</div> : null}
      {managerMessage ? <div className="fa-status-msg" role="status" aria-live="polite">{managerMessage}</div> : null}

      {/* БЛОК A: Поле ввода */}
      <section className="fa-card fa-input-section" aria-labelledby="manager-input-title">
        <div className="fa-section-head">
          <div>
            <h2 id="manager-input-title">Добавить аккаунт 2FA</h2>
            <p id="manager-input-description">Код появится сразу после распознавания, а после добавления сохранится в вашем профиле.</p>
          </div>
          <span className="fa-format-note">Base32 · URI · QR</span>
        </div>
        <div className={`fa-primary-field ${manualError ? 'has-error' : ''}`}>
          <label className="fa-field-label" htmlFor="manual-source">Секрет или ссылка 2FA</label>
          <div className="fa-input-row">
            <div className="fa-input-wrap">
              <input
                id="manual-source"
                name="totp-source"
                className="fa-input-main mono"
                type="text"
                autoComplete="off"
                spellCheck={false}
                aria-invalid={Boolean(manualError)}
                aria-describedby={`manager-input-description${manualError ? ' manual-source-error' : ''}`}
                placeholder="Например, JBSWY3DPEHPK3PXP или otpauth://…"
                value={manualSource}
                onChange={(event) => { setManualSource(event.target.value); setManualError(''); }}
                onKeyDown={handleManualSourceKeyDown}
              />
              <button
                type="button"
                className="fa-btn-qr"
                onClick={() => document.getElementById('qr-upload-manager')?.click()}
                title="Загрузить QR-код"
                aria-label="Загрузить QR-код"
              >
                <AppIcon name="camera" />
              </button>
              <input
                id="qr-upload-manager"
                name="totp-qr"
                aria-label="Файл с QR-кодом"
                className="fa-hidden-file"
                type="file"
                tabIndex={-1}
                accept="image/png,image/jpeg,image/webp"
                onChange={handleQrUploadManager}
              />
            </div>
            <button
              type="button"
              className="fa-btn-add"
              onClick={handleManualSubmit}
              disabled={manualLoading || !manualSource.trim()}
            >
              {manualLoading ? 'Добавление…' : 'Добавить'}
            </button>
          </div>
          {manualError ? <div id="manual-source-error" className="fa-error-box" role="alert">{manualError}</div> : null}
        </div>

        <div className="fa-note-row">
          <label className="fa-field-label" htmlFor="manual-note">Название аккаунта</label>
          <input
            id="manual-note"
            name="account-note"
            className="fa-note-input"
            type="text"
            autoComplete="off"
            placeholder="Например, рабочая почта…"
            value={manualNote}
            onChange={(event) => setManualNote(event.target.value)}
          />
        </div>

        <div className="fa-input-sub">
          <button
            type="button"
            className="fa-help-toggle"
            aria-expanded={helpOpen}
            aria-controls="manager-input-help"
            onClick={() => setHelpOpen((v) => !v)}
          >
            Что можно вставить
            <AppIcon name="chevron-down" className={helpOpen ? 'is-rotated' : ''} />
          </button>
        </div>

        <div id="manager-input-help" className={`fa-help-panel ${helpOpen ? 'is-open' : ''}`}>
          <div className="fa-help-title">Поддерживаемые форматы</div>
          <div className="fa-help-item">
            <span className="fa-help-tag">Base32</span>
            <span>Секретный ключ, например <code>JBSWY3DPEHPK3PXP</code></span>
          </div>
          <div className="fa-help-item">
            <span className="fa-help-tag">URI</span>
            <span>Ссылка вида <code>otpauth://totp/Service:user?secret=…</code></span>
          </div>
          <div className="fa-help-item">
            <span className="fa-help-tag">Текст</span>
            <span>Произвольный текст, содержащий секрет или URI — он будет извлечён автоматически</span>
          </div>
        </div>
      </section>

      {/* БЛОК B: Карточка живого кода */}
      {manualParsed ? (
        <div className={`fa-live-card ${manualLiveRemaining <= 5 ? 'is-urgent' : ''}`}>
          <div className="fa-live-top">
            <span className="fa-live-service">{manualParsed.issuer || manualParsed.label || 'TOTP'}</span>
          </div>
          <div className="fa-live-code-row">
            <button type="button" className="fa-live-code is-clickable" onClick={() => handleLiveCodeCopy(manualLiveCode, 'manager', manualParsed ? { secret: manualParsed.secretBase32, period: manualParsed.period } : null)} title="Скопировать текущий код" aria-label={`Скопировать код ${manualLiveCode}`}>{manualLiveCode}</button>
            <div className="fa-live-timer">
              <div className="fa-live-timer-num">{manualLiveRemaining}</div>
              <div className="fa-live-timer-label">сек</div>
            </div>
          </div>
          <div className="fa-live-bar">
            <div className="fa-live-bar-fill" style={{ width: `${(manualLiveRemaining / manualParsed.period) * 100}%` }} />
          </div>
          <div className="fa-live-bottom">
            <span className="fa-live-meta">{manualParsed.algorithm} · {manualParsed.digits} цифр · {manualParsed.period}с</span>
            {managerLiveCopyMsg ? (
              <span className="fa-btn-copy-msg" aria-live="polite">{managerLiveCopyMsg}</span>
            ) : (
              <button type="button" className="fa-btn-copy" onClick={() => handleLiveCodeCopy(manualLiveCode, 'manager', manualParsed ? { secret: manualParsed.secretBase32, period: manualParsed.period } : null)} disabled={manualLiveCode === '------'}>
                Копировать
              </button>
            )}
          </div>
        </div>
      ) : null}
      {manualParsed ? (
        <SingleSharePanel config={{
          secretBase32: manualParsed.secretBase32,
          issuer: manualParsed.issuer,
          label: manualParsed.label,
          algorithm: manualParsed.algorithm,
          digits: manualParsed.digits,
          period: manualParsed.period,
          fallbackLabel: manualNote || '2FA',
        }} />
      ) : null}

      {/* БЛОК C: Массовый ввод */}
      <section className={`fa-bulk-section ${bulkExpanded ? 'is-open' : ''}`}>
        <button
          type="button"
          className="fa-bulk-header"
          aria-expanded={bulkExpanded}
          aria-controls="manager-bulk-panel"
          onClick={() => setBulkExpanded((v) => !v)}
        >
          <span>
            <span className="fa-bulk-title">Добавить несколько аккаунтов</span>
            <span className="fa-bulk-summary">Сохраните в профиль или оставьте временными</span>
          </span>
          <AppIcon name="chevron-down" className="fa-bulk-chevron" />
        </button>
        <div id="manager-bulk-panel" className="fa-bulk-body">
          <div className="fa-bulk-hint">
            Поддерживаются <code>otpauth://totp/…</code> URI и <code>Base32</code>-секреты в произвольном тексте.
          </div>
          <label className="fa-field-label" htmlFor="manager-bulk-input">Список аккаунтов</label>
          <textarea
            id="manager-bulk-input"
            name="bulk-totp-source"
            className="fa-textarea"
            autoComplete="off"
            spellCheck={false}
            placeholder={'Пример:\ngithub otpauth://totp/…\ntelegram JBSWY3DPEHPK3PXP'}
            value={bulkInput}
            onChange={(event) => {
              setBulkInput(event.target.value);
              setBulkTransformStats(null);
            }}
          />
          <div className="fa-bulk-transform-row">
            <button
              type="button"
              className="fa-btn-transform"
              onClick={handleTransformBulk}
              disabled={!bulkInput.trim()}
            >
              Заменить коды ссылками
            </button>
            <span className="fa-transform-help">
              Заменяет Base32, otpauth:// и чужие URL нашей постоянной ссылкой, сохраняя остальной текст строки.
            </span>
            {bulkTransformStats ? (
              <span className="fa-transform-result">
                Заменено: {bulkTransformStats.replaced}, без изменений: {bulkTransformStats.unchanged}
              </span>
            ) : null}
          </div>
          <div className="fa-bulk-footer">
            <button
              type="button"
              className="fa-btn-add"
              onClick={handleBulkSubmitWithToggle}
              disabled={bulkLoading || !bulkInput.trim()}
              style={{ padding: '10px 24px' }}
            >
              {bulkLoading ? 'Добавление…' : 'Добавить все'}
            </button>
            <div className="fa-save-row">
              <label className="fa-toggle">
                <input type="checkbox" aria-label="Сохранять аккаунты в профиль" checked={bulkSaveToProfile} onChange={(e) => setBulkSaveToProfile(e.target.checked)} />
                <span className="fa-toggle-slider" />
              </label>
              <span>Сохранять в профиль</span>
            </div>
          </div>
          {bulkResult ? (
            <>
              <div className="fa-bulk-footer">
                <span className="fa-bulk-stats">
                  Создано: <strong>{bulkResult.created.length}</strong>, пропущено: <strong>{bulkResult.skipped.length}</strong>, ошибок: <strong>{bulkResult.errors.length}</strong>
                </span>
              </div>
              {bulkResult.errors.length > 0 ? (
                <div className="fa-bulk-errors">
                  <div className="fa-bulk-errors-title">Ошибки парсинга</div>
                  {bulkResult.errors.map((item, index) => (
                    <div key={`bulk-error-${index}`} className="fa-bulk-errors-item">строка {item.line}: {item.reason}</div>
                  ))}
                </div>
              ) : null}
              {bulkResult.skipped.length > 0 ? (
                <div className="fa-bulk-errors">
                  <div className="fa-bulk-errors-title">Пропущенные строки</div>
                  {bulkResult.skipped.map((item, index) => (
                    <div key={`bulk-skipped-${index}`} className="fa-bulk-errors-item">строка {item.line}: {item.reason}</div>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      {/* БЛОК D: Список аккаунтов */}
      <section className="fa-accs">
        <div className="fa-accs-header">
          <div className="fa-accs-title">
            Сохранённые
            <span className="fa-accs-count">{filteredRuntimeAccounts.length}</span>
          </div>
          <input
            id="saved-account-search"
            name="saved-account-search"
            className="fa-search"
            type="search"
            autoComplete="off"
            aria-label="Поиск по сохранённым аккаунтам"
            placeholder="Поиск по имени, секрету или заметке…"
            value={searchInput}
            onChange={(event) => { setSearchInput(event.target.value); setCurrentPage(1); }}
          />
        </div>

        {/* Тулбар массовых действий */}
        <div className="fa-accs-toolbar">
          <label>
            <input
              type="checkbox"
              checked={filteredRuntimeAccounts.length > 0 && filteredRuntimeAccounts.every((a) => selectedIds.has(a.id))}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedIds((prev) => { const s = new Set(prev); filteredRuntimeAccounts.forEach((a) => s.add(a.id)); return s; });
                } else {
                  setSelectedIds((prev) => { const s = new Set(prev); filteredRuntimeAccounts.forEach((a) => s.delete(a.id)); return s; });
                }
              }}
            />
            Выделить все
          </label>
          {selectedIds.size > 0 ? (
            <button type="button" className="fa-btn-mass-del" onClick={handleMassDelete}>
              Удалить выбранные ({selectedIds.size})
              </button>
          ) : null}
          <div className="fa-toolbar-spacer" />
          <ExportControls
            format={serverExportFormat}
            selectedCount={selectedIds.size}
            onFormatChange={setServerExportFormat}
            onExport={handleExportServer}
          />
          <div className="fa-per-page">
            <label className="fa-per-page-label" htmlFor="saved-per-page">На странице:</label>
            <select id="saved-per-page" name="saved-accounts-per-page" value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>
        <AccountTableHeader />

        {accountsLoading ? (
          <div className="fa-accs-empty" role="status">Загрузка аккаунтов…</div>
        ) : null}

        {!accountsLoading && filteredRuntimeAccounts.length === 0 ? (
          <div className="fa-accs-empty">
            {accounts.length === 0 ? 'Нет сохранённых аккаунтов — добавьте первый' : 'Ничего не найдено'}
          </div>
        ) : null}

        {!accountsLoading && paginatedAccounts.map((account) => {
          const isUrgent = account.remaining_seconds > 0 && account.remaining_seconds <= 5;
          const rowKey = `server-${account.id}`;
          const isExpanded = expandedRows.has(rowKey);
          const note = account.account_note
            && account.account_note !== account.display_name
            && !/^Аккаунт\s+#?\d+$/i.test(account.account_note)
            ? account.account_note
            : undefined;
          return (
            <AccountTableRow
              key={account.id}
              displayName={account.display_name}
              note={note}
              secretBase32={account.secret_base32}
              currentCode={account.current_code}
              remainingSeconds={account.remaining_seconds}
              copyStatus={managerCopyMap[account.id]}
              selected={selectedIds.has(account.id)}
              urgent={isUrgent}
              expanded={isExpanded}
              avatarColor={avatarColor(account.display_name)}
              shareConfig={serverShareConfig(account)}
              onSelect={() => toggleSelectAccount(account.id)}
              onToggle={() => toggleExpandRow(rowKey)}
              onCopyCode={() => handleRuntimeCodeCopy(account.id, account.current_code)}
              onDelete={() => handleDeleteAccount(account.id)}
              details={(
                <>
                  <div className="fa-acc-detail"><span className="fa-detail-label">Алгоритм:</span> {account.algorithm}</div>
                  <div className="fa-acc-detail"><span className="fa-detail-label">Цифр:</span> {account.digits}</div>
                  <div className="fa-acc-detail"><span className="fa-detail-label">Период:</span> {account.period}с</div>
                  {account.issuer ? <div className="fa-acc-detail"><span className="fa-detail-label">Issuer:</span> {account.issuer}</div> : null}
                  {account.label ? <div className="fa-acc-detail"><span className="fa-detail-label">Label:</span> {account.label}</div> : null}
                  {account.source_raw ? <div className="fa-acc-detail"><span className="fa-detail-label">Исходная строка:</span> <span className="mono">{account.source_raw}</span></div> : null}
                  <div className="fa-acc-detail"><span className="fa-detail-label">Создан:</span> {account.created_at ? new Date(account.created_at).toLocaleString('ru-RU') : '—'}</div>
                </>
              )}
            />
          );
        })}

        {/* Пагинация */}
        {totalPages > 1 ? (
          <div className="fa-pagination">
            <span className="fa-pagination-info">
              {((currentPage - 1) * perPage) + 1}–{Math.min(currentPage * perPage, filteredRuntimeAccounts.length)} из {filteredRuntimeAccounts.length}
            </span>
            <div className="fa-pagination-controls">
              <button type="button" className="fa-pagination-btn" aria-label="Предыдущая страница" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}><AppIcon name="arrow-left" /></button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let page: number;
                if (totalPages <= 7) { page = i + 1; }
                else if (currentPage <= 4) { page = i + 1; }
                else if (currentPage >= totalPages - 3) { page = totalPages - 6 + i; }
                else { page = currentPage - 3 + i; }
                return (
                  <button key={page} type="button" className={`fa-pagination-btn ${page === currentPage ? 'is-active' : ''}`} onClick={() => setCurrentPage(page)}>
                    {page}
                  </button>
                );
              })}
              <button type="button" className="fa-pagination-btn" aria-label="Следующая страница" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}><AppIcon name="arrow-right" /></button>
            </div>
          </div>
        ) : null}
        <div className="fa-table-footer">
          <span className="fa-table-footer-label">Выбрано: {selectedIds.size}</span>
          <ExportControls
            format={serverExportFormat}
            selectedCount={selectedIds.size}
            onFormatChange={setServerExportFormat}
            onExport={handleExportServer}
          />
        </div>
      </section>

      {/* БЛОК E: Временные аккаунты (массовый ввод без сохранения) */}
      {localAccounts.length > 0 ? (
        <section className="fa-accs fa-accs-spaced">
          <div className="fa-accs-header">
            <div className="fa-accs-title">
              Временные
              <span className="fa-accs-count">{filteredLocalAccounts.length}</span>
            </div>
            <input
              id="temporary-account-search"
              name="temporary-account-search"
              className="fa-search"
              type="search"
              autoComplete="off"
              aria-label="Поиск по временным аккаунтам"
              placeholder="Поиск по аккаунтам…"
              value={localSearchInput}
              onChange={(event) => { setLocalSearchInput(event.target.value); setLocalCurrentPage(1); }}
            />
          </div>

          <div className="fa-accs-toolbar">
            <label>
              <input
                type="checkbox"
                checked={filteredLocalAccounts.length > 0 && filteredLocalAccounts.every((a) => selectedLocalIds.has(a.localId))}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedLocalIds((prev) => { const s = new Set(prev); filteredLocalAccounts.forEach((a) => s.add(a.localId)); return s; });
                  } else {
                    setSelectedLocalIds((prev) => { const s = new Set(prev); filteredLocalAccounts.forEach((a) => s.delete(a.localId)); return s; });
                  }
                }}
              />
              Выделить все
            </label>
            {selectedLocalIds.size > 0 ? (
              <button type="button" className="fa-btn-mass-del" onClick={handleMassDeleteLocal}>
                Удалить выбранные ({selectedLocalIds.size})
              </button>
            ) : null}
            <div className="fa-toolbar-spacer" />
            <ExportControls
              format={localExportFormat}
              selectedCount={selectedLocalIds.size}
              onFormatChange={setLocalExportFormat}
              onExport={handleExportLocal}
            />
          </div>
          <AccountTableHeader />

          {filteredLocalAccounts.length === 0 ? (
            <div className="fa-accs-empty">Ничего не найдено</div>
          ) : null}

          {paginatedLocalAccounts.map((account) => {
            const isUrgent = account.remaining_seconds > 0 && account.remaining_seconds <= 5;
            const rowKey = `local-${account.localId}`;
            const isExpanded = expandedRows.has(rowKey);
            return (
              <AccountTableRow
                key={account.localId}
                displayName={account.displayName}
                note={account.note}
                secretBase32={account.secretBase32}
                currentCode={account.current_code}
                remainingSeconds={account.remaining_seconds}
                copyStatus={localCopyMap[account.localId]}
                selected={selectedLocalIds.has(account.localId)}
                urgent={isUrgent}
                expanded={isExpanded}
                avatarColor={avatarColor(account.displayName)}
                shareConfig={localShareConfig(account)}
                onSelect={() => toggleSelectLocalAccount(account.localId)}
                onToggle={() => toggleExpandRow(rowKey)}
                onCopyCode={() => handleLocalCodeCopy(account.localId, account.current_code)}
                onDelete={() => handleDeleteLocalAccount(account.localId)}
                details={(
                  <>
                    <div className="fa-acc-detail"><span className="fa-detail-label">Алгоритм:</span> {account.algorithm}</div>
                    <div className="fa-acc-detail"><span className="fa-detail-label">Цифр:</span> {account.digits}</div>
                    <div className="fa-acc-detail"><span className="fa-detail-label">Период:</span> {account.period}с</div>
                    {account.issuer ? <div className="fa-acc-detail"><span className="fa-detail-label">Issuer:</span> {account.issuer}</div> : null}
                    {account.label ? <div className="fa-acc-detail"><span className="fa-detail-label">Label:</span> {account.label}</div> : null}
                    {account.sourceRaw ? <div className="fa-acc-detail"><span className="fa-detail-label">Исходная строка:</span> <span className="mono">{account.sourceRaw}</span></div> : null}
                  </>
                )}
              />
            );
          })}

          {localTotalPages > 1 ? (
            <div className="fa-pagination">
              <span className="fa-pagination-info">
                {((localCurrentPage - 1) * perPage) + 1}–{Math.min(localCurrentPage * perPage, filteredLocalAccounts.length)} из {filteredLocalAccounts.length}
              </span>
              <div className="fa-pagination-controls">
                <button type="button" className="fa-pagination-btn" aria-label="Предыдущая страница" disabled={localCurrentPage <= 1} onClick={() => setLocalCurrentPage((p) => p - 1)}><AppIcon name="arrow-left" /></button>
                {Array.from({ length: Math.min(localTotalPages, 7) }, (_, i) => {
                  let page: number;
                  if (localTotalPages <= 7) { page = i + 1; }
                  else if (localCurrentPage <= 4) { page = i + 1; }
                  else if (localCurrentPage >= localTotalPages - 3) { page = localTotalPages - 6 + i; }
                  else { page = localCurrentPage - 3 + i; }
                  return (
                    <button key={page} type="button" className={`fa-pagination-btn ${page === localCurrentPage ? 'is-active' : ''}`} onClick={() => setLocalCurrentPage(page)}>
                      {page}
                    </button>
                  );
                })}
                <button type="button" className="fa-pagination-btn" aria-label="Следующая страница" disabled={localCurrentPage >= localTotalPages} onClick={() => setLocalCurrentPage((p) => p + 1)}><AppIcon name="arrow-right" /></button>
              </div>
            </div>
          ) : null}
          <div className="fa-table-footer">
            <span className="fa-table-footer-label">Выбрано: {selectedLocalIds.size}</span>
            <ExportControls
              format={localExportFormat}
              selectedCount={selectedLocalIds.size}
              onFormatChange={setLocalExportFormat}
              onExport={handleExportLocal}
            />
          </div>
        </section>
      ) : null}
    </>
  );

  return (
    <div className="u2-shell u2-shellWithSidebar">
      <a className="fa-skip-link" href="#main-content">К основному содержимому</a>
      {/* Единый Web Component <portal-sidebar>: брендинг, список сервисов
          и user-карточка отрисовываются в shadow DOM из portal-config. */}
      <portal-sidebar current-slug={ACTIVE_SERVICE_SLUG} data-theme={darkMode ? 'dark' : 'light'} />

      <div className="u2-body">
        {/* Единый верхний бар портала: бренд 3a-fb.info + баланс/вход (общий для всех проектов) */}
        <portal-topbar
          ref={portalTopbarRef}
          service-name="2FA"
          current-slug={ACTIVE_SERVICE_SLUG}
          theme-enabled="true"
          data-theme={darkMode ? 'dark' : 'light'}
        />

        <main id="main-content" className="u2-main" tabIndex={-1}>
          <div className="u2-container">
            {authChecking ? (
              <div className="fa-auth-loading" role="status">Проверяем авторизацию…</div>
            ) : (
              <div className="fa-page-stack">
                <header className="fa-page-intro">
                  <div className="fa-page-heading">
                    <span className="fa-page-mark" aria-hidden="true">
                      <AppIcon name="shield" />
                    </span>
                    <div>
                      <h1>Генератор кодов 2FA</h1>
                      <p>
                        {isAuthorized
                          ? `Персональный менеджер · ${authUserId}`
                          : 'Генератор одноразовых кодов — вставьте секрет и получите TOTP без лишних шагов.'}
                      </p>
                    </div>
                  </div>
                  <span className="fa-context-chip">
                    <AppIcon name="check" />
                    {isAuthorized ? 'Хранение в профиле' : 'Локально в браузере'}
                  </span>
                </header>
                {isAuthorized ? managerMain : publicMain}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;
